import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, Pencil, X, Check, AlertTriangle, ChevronRight, ChevronDown, GripVertical } from 'lucide-react';
import type { Chapter, Volume } from '../../../types';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import { errorMessage, notifyError } from '../../../utils/notify';
import '../styles/volumeManager.css';

/** 草稿里的一个分卷（id 可能是新建时生成的临时 id） */
interface DraftVolume {
  id: string;
  title: string;
  description: string;
  /** 是否为"建作品时自动创建的默认卷"（不可删除） */
  isDefault: boolean;
  /** 尚未落库的新卷（保存时才创建） */
  isNew?: boolean;
}

/** 草稿里的一个章节：`volumeId` 表示它当前归属哪一卷 */
interface DraftChapter {
  id: string;
  volumeId: string;
  title: string;
  summary: string;
  wordCount: number;
}

interface VolumeManagerModalProps {
  bookId: string;
  volumes: Volume[];
  chaptersMap: Record<string, Chapter[]>;
  /** 打开时直接进入重命名的卷（右键菜单「编辑分卷」用） */
  focusVolumeId?: string | null;
  onClose: () => void;
  /** 保存成功后的回调（父层刷新数据） */
  onSaved: () => Promise<void>;
  /** 立即删除单个章节（破坏性动作，单独确认） */
  onDeleteChapter: (chapter: Chapter) => Promise<void>;
}

/**
 * 卷目管理：卷的增删改 + 章节归属与顺序，**草稿式编辑、统一保存**。
 *
 * 三条设计决定，都是被实际问题推出来的：
 *
 * 1. **草稿 + 显式保存**（而不是即时生效）。卷目调整往往要连着改好几处（建卷、移章节、
 *    拖动顺序），逐个即时落库会留下"编号断档/重复"的中间状态，而且改错了没法回头。
 * 现在所有改动都留在本地草稿里，点「保存」时才一次性原子提交（服务端 `POST /books/:id/structure`）。
 *
 * 2. **保存时全书重编号**。章节编号全书唯一（`uq_chapters_book_chapter` = book_id + chapter_number），
 * 顺序由「卷的顺序 + 卷内顺序」决定。因此跨卷移动或拖动排序后，必须重编号 ——
 * 否则"第 N 章"会与实际阅读顺序脱节。服务端按提交顺序从 1 连续编号。
 *
 * 3. **各卷可同时展开**（此前用单个 expandedId，一次只能开一个，对比两卷内容很别扭）。
 */
export function VolumeManagerModal({
  bookId, volumes, chaptersMap, focusVolumeId, onClose, onSaved, onDeleteChapter,
}: VolumeManagerModalProps) {
  // ── 草稿状态 ──
  const [draftVolumes, setDraftVolumes] = useState<DraftVolume[]>(() =>
    volumes.map(v => ({ id: v.id, title: v.title, description: v.description ?? '', isDefault: v.is_default })));
  const [draftChapters, setDraftChapters] = useState<DraftChapter[]>(() =>
    volumes.flatMap(v => (chaptersMap[v.id] ?? []).map(c => ({
      id: c.id, volumeId: v.id, title: c.title, summary: c.summary ?? '', wordCount: c.word_count ?? 0,
    }))));
  /** 展开的卷（可多个同时展开） */
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(volumes.map(v => v.id)));
  const [editingVolumeId, setEditingVolumeId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');

  // 由右键菜单「编辑分卷」打开时，直接进入该卷的重命名态
  useEffect(() => {
    if (!focusVolumeId) return;
    const vol = volumes.find(v => v.id === focusVolumeId);
    if (vol) {
      setEditingVolumeId(vol.id);
      setEditTitle(vol.title);
      setExpanded(prev => new Set(prev).add(vol.id));
    }
    // 仅在打开时按 focusVolumeId 定位一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusVolumeId]);
  const [deletingVolumeId, setDeletingVolumeId] = useState<string | null>(null);
  const [deleteMode, setDeleteMode] = useState<'move' | 'delete-chapters'>('move');
  const [moveTarget, setMoveTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  /** 有未保存改动时点了关闭 → 弹内联确认条（不用原生 window.confirm：
      它与应用视觉不一致，而且在自动化测试里会被直接 dismiss 掉） */
  const [confirmingClose, setConfirmingClose] = useState(false);

  /** 新建卷的临时 id 计数器 */
  const newSeqRef = useRef(0);
  const dragChapterIdRef = useRef<string | null>(null);
  const [dragOver, setDragOver] = useState<{ chapterId?: string; volumeId?: string } | null>(null);

  const markDirty = () => setDirty(true);

  // ── 派生：全书的最终阅读顺序 = 按卷顺序，再按卷内顺序 ──
  const chapterOrder = useMemo(() => {
    const order: DraftChapter[] = [];
    for (const v of draftVolumes) {
      for (const c of draftChapters) {
        if (c.volumeId === v.id) order.push(c);
      }
    }
    return order;
  }, [draftVolumes, draftChapters]);

  const chaptersOfVolume = (volumeId: string) => draftChapters.filter(c => c.volumeId === volumeId);

  /** 全书序号（用于显示"第 N 章"） */
  const numberOf = (chapterId: string) => chapterOrder.findIndex(c => c.id === chapterId) + 1;

  // ── 卷操作 ──
  const addVolume = () => {
    newSeqRef.current += 1;
    const id = `new-volume-${newSeqRef.current}`;
    setDraftVolumes(prev => [...prev, { id, title: `第${prev.length + 1}卷`, description: '', isDefault: false, isNew: true }]);
    setExpanded(prev => new Set(prev).add(id));
    setEditingVolumeId(id);
    setEditTitle(`第${draftVolumes.length + 1}卷`);
    markDirty();
  };

  const commitRename = (volumeId: string) => {
    const title = editTitle.trim();
    if (title) {
      setDraftVolumes(prev => prev.map(v => (v.id === volumeId ? { ...v, title } : v)));
      markDirty();
    }
    setEditingVolumeId(null);
  };

  const requestDeleteVolume = (volumeId: string) => {
    const index = draftVolumes.findIndex(v => v.id === volumeId);
    const others = draftVolumes.filter(v => v.id !== volumeId);
    const fallback = others[index - 1] ?? others[index] ?? others[0];
    setDeletingVolumeId(volumeId);
    setDeleteMode('move');
    setMoveTarget(fallback?.id ?? '');
  };

  const confirmDeleteVolume = () => {
    if (!deletingVolumeId) return;
    const target = moveTarget || draftVolumes.find(v => v.id !== deletingVolumeId)?.id || '';
    if (deleteMode === 'move' && !target) {
      notifyError('没有可转移的目标分卷');
      return;
    }
    setDraftChapters(prev => {
      if (deleteMode === 'delete-chapters') {
        return prev.filter(c => c.volumeId !== deletingVolumeId);
      }
      return prev.map(c => (c.volumeId === deletingVolumeId ? { ...c, volumeId: target } : c));
    });
    setDraftVolumes(prev => prev.filter(v => v.id !== deletingVolumeId));
    setDeletingVolumeId(null);
    markDirty();
  };

  // ── 章节操作 ──

  /**
   * 把章节移动到"某个位置"。
   *
   * `beforeChapterId` 为空表示追加到目标卷末尾。
   * 由于章节顺序存的是**数组位置**（而非显式序号），一次移动就是"先从数组里摘掉、再插到新位置"，
   * 然后由 `chapterOrder` 自动重算全书顺序 —— 无需手工维护任何序号。
   */
  const moveChapter = useCallback((chapterId: string, targetVolumeId: string, beforeChapterId: string | null) => {
    setDraftChapters(prev => {
      const moving = prev.find(c => c.id === chapterId);
      if (!moving) return prev;
      const rest = prev.filter(c => c.id !== chapterId);
      const updated = { ...moving, volumeId: targetVolumeId };
      if (!beforeChapterId) {
        // 插到目标卷现有章节之后（保持"在卷内追加"的直觉）
        const lastIndex = rest.map(c => c.volumeId).lastIndexOf(targetVolumeId);
        if (lastIndex === -1) return [...rest, updated];
        const next = [...rest];
        next.splice(lastIndex + 1, 0, updated);
        return next;
      }
      const at = rest.findIndex(c => c.id === beforeChapterId);
      if (at === -1) return [...rest, updated];
      const next = [...rest];
      next.splice(at, 0, updated);
      return next;
    });
    markDirty();
  }, []);

  const removeChapter = async (chapter: DraftChapter) => {
    const original = volumes.flatMap(v => chaptersMap[v.id] ?? []).find(c => c.id === chapter.id);
    if (!original) return;
    // 删除章节是不可撤销的破坏性动作，且本接口不做章节删除 —— 立即落库并单独确认
    await onDeleteChapter(original);
    setDraftChapters(prev => prev.filter(c => c.id !== chapter.id));
    markDirty();
  };

  // ── 保存 ──
  const canSave = draftVolumes.length > 0;

  const handleSave = async () => {
    if (!canSave) {
      notifyError('至少要保留一个分卷');
      return;
    }
    setBusy(true);
    try {
      // 新卷需要先真正创建，拿到 id 后章节才能归属过去
      const idMap = new Map<string, string>();
      const finalVolumes: { id: string; title: string; description: string }[] = [];
      for (const v of draftVolumes) {
        if (v.isNew) {
          const created = await window.api.createVolume({ bookId, title: v.title.trim() || '未命名分卷', description: v.description });
          idMap.set(v.id, created.id);
          finalVolumes.push({ id: created.id, title: v.title.trim() || '未命名分卷', description: v.description });
        } else {
          finalVolumes.push({ id: v.id, title: v.title.trim() || '未命名分卷', description: v.description });
        }
      }
      const finalChapters = chapterOrder.map(c => ({
        id: c.id,
        volumeId: idMap.get(c.volumeId) ?? c.volumeId,
        title: c.title,
        summary: c.summary,
      }));
      await window.api.commitBookStructure({ bookId, volumes: finalVolumes, chapters: finalChapters });
      setDirty(false);
      // 先落 busy=false 再关弹窗：这样"保存结束"是一个**可观察**的状态
      // （aria-busy / 按钮文案复位），调用方与测试能确定本次提交已完成，
      // 而不是在"弹窗已卸载但后台仍在刷新"的中间态里继续。
      setBusy(false);
      onClose();
      // 数据刷新放在关闭之后：onSaved 要重新拉全书卷章（可达数秒），
      // 若等它跑完才关闭，遮罩会一直挡着侧栏（表现为"点了没反应"）。
      await onSaved();
      return;
    } catch (e) {
      notifyError('保存卷目失败：' + errorMessage(e));
    }
    setBusy(false);
  };

  const requestClose = () => {
    if (busy) return;
    if (dirty) { setConfirmingClose(true); return; }
    onClose();
  };
  const dismiss = useModalDismiss(() => { if (!dirty && !busy) onClose(); });

  // Esc：先收掉"未保存"确认条，其次按 requestClose 处理
  // （useModalDismiss 的 Esc 只在无改动时才会真正关闭）
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (confirmingClose) { setConfirmingClose(false); return; }
      requestClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, busy, confirmingClose]);

  return (
    <div className="modal-overlay" {...dismiss}>
      <div className="modal-content vm-modal" aria-busy={busy}>
        <div className="vm-head">
          <div>
            <h2 className="modal-title vm-title">卷目管理</h2>
            <p className="vm-hint">
              拖动章节可调整顺序与所属分卷；编号会按新顺序重排。
              {dirty && <span className="vm-dirty">改动尚未保存</span>}
            </p>
          </div>
          <button className="vm-add" onClick={addVolume} disabled={busy}>
            <Plus size={13} /> 新建分卷
          </button>
        </div>

        <div className="vm-list">
          {draftVolumes.map((vol, vIndex) => {
            const chapters = chaptersOfVolume(vol.id);
            const words = chapters.reduce((sum, c) => sum + c.wordCount, 0);
            const isOpen = expanded.has(vol.id);
            const isDeleting = deletingVolumeId === vol.id;
            const isEditing = editingVolumeId === vol.id;
            return (
              <div key={vol.id} className="vm-item">
                <div className="vm-item-head">
                  <button
                    className="vm-expand"
                    aria-label={isOpen ? '收起章节' : '展开章节'}
                    aria-expanded={isOpen}
                    onClick={() => setExpanded(prev => {
                      const next = new Set(prev);
                      if (next.has(vol.id)) next.delete(vol.id); else next.add(vol.id);
                      return next;
                    })}
                  >
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>

                  {isEditing ? (
                    <input
                      className="vm-title-input"
                      value={editTitle}
                      autoFocus
                      onChange={(e) => setEditTitle(e.target.value)}
                      onBlur={() => commitRename(vol.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename(vol.id);
                        if (e.key === 'Escape') setEditingVolumeId(null);
                      }}
                      aria-label="分卷名称"
                    />
                  ) : (
                    <span className="vm-item-title">第{vIndex + 1}卷 {vol.title}</span>
                  )}

                  {vol.isDefault && <span className="vm-badge">默认</span>}
                  {vol.isNew && <span className="vm-badge vm-badge-new">待创建</span>}
                  <span className="vm-item-meta">{chapters.length}章 · {words}字</span>

                  <div className="vm-item-actions">
                    <button
                      className="vm-icon"
                      title="重命名"
                      aria-label="重命名分卷"
                      disabled={busy}
                      onClick={() => { setEditingVolumeId(vol.id); setEditTitle(vol.title); }}
                    >
                      <Pencil size={13} />
                    </button>
                    {/* 默认卷不给删除入口：它通常装着全书，服务端也会拒绝 */}
                    {!vol.isDefault && (
                      <button
                        className="vm-icon vm-icon-danger"
                        title="删除分卷"
                        aria-label="删除分卷"
                        disabled={busy}
                        onClick={() => requestDeleteVolume(vol.id)}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>

                {isDeleting && (
                  <div className="vm-delete">
                    <div className="vm-delete-title">删除「第{vIndex + 1}卷 {vol.title}」</div>
                    <label className="vm-delete-option">
                      <input type="radio" checked={deleteMode === 'move'} onChange={() => setDeleteMode('move')} />
                      保留 {chapters.length} 个章节，转移到
                      <select
                        className="vm-select"
                        value={moveTarget}
                        disabled={deleteMode !== 'move'}
                        onChange={(e) => setMoveTarget(e.target.value)}
                        aria-label="转移目标分卷"
                      >
                        {draftVolumes.filter(v => v.id !== vol.id).map((v) => (
                          <option key={v.id} value={v.id}>第{draftVolumes.findIndex(x => x.id === v.id) + 1}卷 {v.title}</option>
                        ))}
                      </select>
                    </label>
                    <label className="vm-delete-option">
                      <input type="radio" checked={deleteMode === 'delete-chapters'} onChange={() => setDeleteMode('delete-chapters')} />
                      连同 {chapters.length} 个章节一起删除（不可撤销）
                    </label>
                    <div className="vm-delete-actions">
                      <button className="vm-btn" onClick={() => setDeletingVolumeId(null)} disabled={busy}>取消</button>
                      <button className="vm-btn vm-btn-danger" onClick={confirmDeleteVolume} disabled={busy}>确认删除</button>
                    </div>
                  </div>
                )}

                {isOpen && !isDeleting && (
                  <div className="vm-chapters">
                    {chapters.length === 0 && <div className="vm-empty">该分卷暂无章节</div>}
                    {chapters.map((chap) => (
                      <div
                        key={chap.id}
                        className={`vm-chapter${dragOver?.chapterId === chap.id ? ' is-drop-target' : ''}`}
                        draggable={!busy}
                        onDragStart={() => { dragChapterIdRef.current = chap.id; }}
                        onDragEnd={() => { dragChapterIdRef.current = null; setDragOver(null); }}
                        onDragOver={(e) => { e.preventDefault(); setDragOver({ chapterId: chap.id }); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const movingId = dragChapterIdRef.current;
                          setDragOver(null);
                          if (movingId && movingId !== chap.id) moveChapter(movingId, vol.id, chap.id);
                        }}
                      >
                        <span className="vm-grip" title="拖动调整顺序"><GripVertical size={12} /></span>
                        <span className="vm-chapter-title">
                          第{numberOf(chap.id)}章 {chap.title}
                        </span>
                        <span className="vm-chapter-words">{chap.wordCount}字</span>
                        <select
                          className="vm-select"
                          value={vol.id}
                          disabled={busy}
                          aria-label={`把「${chap.title}」移到其它分卷`}
                          onChange={(e) => moveChapter(chap.id, e.target.value, null)}
                        >
                          {draftVolumes.map((v, vi) => (
                            <option key={v.id} value={v.id}>第{vi + 1}卷 {v.title}</option>
                          ))}
                        </select>
                        <button
                          className="vm-icon vm-icon-danger"
                          title="删除章节"
                          aria-label="删除章节"
                          disabled={busy}
                          onClick={() => void removeChapter(chap)}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                    {/* 卷尾放置区：拖到卷的空白处即追加到该卷末尾 */}
                    <div
                      className={`vm-chapter-drop${dragOver?.volumeId === vol.id ? ' is-drop-target' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); setDragOver({ volumeId: vol.id }); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const movingId = dragChapterIdRef.current;
                        setDragOver(null);
                        if (movingId) moveChapter(movingId, vol.id, null);
                      }}
                    >
                      拖到此处 → 移到本卷末尾
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!canSave && (
          <p className="vm-warn">
            <AlertTriangle size={13} /> 至少要保留一个分卷
          </p>
        )}

        {/* 有未保存改动时点关闭 → 内联确认，避免误丢改动 */}
        {confirmingClose && (
          <div className="vm-confirm-close">
            <span>有未保存的卷目改动，确定放弃吗？</span>
            <div className="vm-confirm-close-actions">
              <button className="vm-btn" onClick={() => setConfirmingClose(false)}>继续编辑</button>
              <button className="vm-btn vm-btn-danger" onClick={onClose}>放弃改动</button>
            </div>
          </div>
        )}

        <div className="modal-actions vm-actions">
          <button className="btn-cancel" onClick={requestClose} disabled={busy}>
            <X size={13} /> 取消
          </button>
          <button className="btn-confirm" onClick={() => void handleSave()} disabled={busy || !canSave} aria-label="保存卷目">
            <Check size={13} /> {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}
